<template>
    <div class="layers-container layout-main">
        <c-collapse :title="settings.title" resizable>
            <template #header>
                <layers-header
                    :start-with-canva="flags.startWithCanva"
                    @import-layers="importLayers"
                    @user-updated="() => emit('user-updated')"
                    @open-find-layers="openFindLayers"
                />
                <find-in-layers
                    ref="findInLayersRef"
                    :enable="flags.enableFindInLayers"
                    @layer-selected="setActiveElement"
                />
            </template>
            <layers-tree @tree-item-moved="layerTreeItemMove" />
        </c-collapse>
    </div>
</template>

<script lang="ts" setup>
import { useLayers } from './composables/useLayers';
import store from '@/store';

import type { LayersSettings, LayersFlags, LayersTemplate } from './types';
import type { CanvasEx } from '@/typings/canvas';

import CCollapse from '@/components/CCollapse.vue';
import LayersTree from './tree/LayersTree.vue';
import { FindInLayersRef } from './header';
import LayersHeader from './header/LayersHeader.vue';
import FindInLayers from './header/FindInLayers.vue';
import { onUnmounted, Ref, ref } from 'vue';

const emit = defineEmits<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: 'import-layers', value: any): void;
    (event: 'tree-item-moved'): void;
    (event: 'user-updated'): void;
}>();

const props = withDefaults(
    defineProps<{
        settings: LayersSettings;
        flags: LayersFlags;
        canvas: CanvasEx | null;
        auxCanvas?: CanvasEx | null;
        template?: LayersTemplate;
    }>(),
    {
        flags: () => ({
            allowSorting: true,
            allowChangingPrintArea: true,
            allowChangingOverlay: true,
            showGroups: true,
            isAbleToGroupOrUnGroup: true,
            enablePageEps: true,
            isAdminTemplateProduct: false,
            startWithCanva: false,
            enableFindInLayers: true
        })
    }
);

const {
    setActiveElement,
    singleGroupSelection,
    initGroupSelectionFromPageEps,
    importLayers,
    refreshLayerPanel
} = useLayers(props, emit);

defineExpose({
    setActiveElement,
    singleGroupSelection,
    initGroupSelectionFromPageEps,
    refreshLayerPanel
});

const findInLayersRef: Ref<FindInLayersRef | null> = ref(null);

onUnmounted(() => {
    store.dispatch('storeLayers/clear');
});

const layerTreeItemMove = () => {
    emit('tree-item-moved');
};

const openFindLayers = () => {
    findInLayersRef?.value?.toggle();
};
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/bootstrap.scss';

$icon-size: 20px;
$icon-font-size: 14px;

::v-deep .c-button-wrapper.btn.dropdown-toggle.btn-empty.btn-md {
    color: #87acd5 !important;
    font-weight: 700 !important;
    &:focus {
        border: 1.5px solid #87acd5 !important;
        border-radius: 4px;
    }
}

::v-deep .dropdown-menu.show {
    position: absolute;
    transform: translate3d(-174px, 38px, 0px) !important;
    top: 0px !important;
    left: 0px !important;
    will-change: transform !important;
}

::v-deep .dropdown-item {
    &:active {
        background-color: transparent !important;
        color: black !important;
    }
}

.layers-container {
    --primary-blue: #87add5;
    --secondary-color: #a7aab4;
    --secondary-hover-color: #868686;

    background: white;

    ::v-deep .collapse-header {
        position: relative;
    }

    ::v-deep .children-wrapper-1 {
        .col-layer-element {
            margin-left: 0.2rem !important;
        }
    }

    ::v-deep .children-wrapper-2 {
        .col-layer-element {
            margin-left: 1.3rem !important;
        }
    }

    ::v-deep .children-wrapper-3 {
        .col-layer-element {
            margin-left: 2.4rem !important;
        }
    }

    ::v-deep .item-wrapper-some-control {
        .col-layer-controls {
            width: 50px;
        }
    }

    ::v-deep .item-wrapper-all-control {
        .col-layer-controls {
            width: 120px;
        }
    }

    ::v-deep .item-wrapper-page-eps-disabled {
        .layer-canvas-element .col-layer-element > .icon {
            display: none !important;
        }

        .layer-group .col-layer-element {
            margin-left: -2.1rem;
        }
    }

    ::v-deep .items-wrapper {
        border: none;
        padding: 0;
        padding-top: 0.4rem;
        padding-bottom: 0.4rem;

        .layers-element-title {
            .editable-input {
                background: #f2f2f2;
                width: 100%;
                border: none;
                color: black;
            }
        }

        .element-icon {
            align-self: center;
            color: var(--primary-blue);
            fill: var(--primary-blue);
        }

        .rename-btn {
            font-size: 10px;
        }

        .icon {
            width: $icon-size;
            height: $icon-size;
            float: left;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: $icon-font-size;
        }

        .open-close-icon {
            fill: #a7aab4;
        }

        &.selected {
            color: white;
            fill: white;

            &:not(.print-area) {
                background: #87acd5;
            }

            &.print-area {
                background: repeating-linear-gradient(45deg, #87acd5, #87acd5 10px, #7b9cc0 10px, #7b9cc0 20px);
            }

            &.items-page-eps {
                background: #95a7bb;
            }

            .action-icon,
            .action-icon-selected {
                fill: #fff;
            }

            .element-icon {
                color: white;
                fill: white !important;
            }

            .open-close-icon {
                fill: white;
            }
        }
    }
}
</style>
